import { File } from 'expo-file-system';
import { format } from 'date-fns';
import { and, eq, getTableColumns, inArray, isNull, isNotNull, notExists, or, sql } from 'drizzle-orm';
import { alias, getTableConfig, type SQLiteColumn, type SQLiteTable } from 'drizzle-orm/sqlite-core';
import { getErrorMessage } from '@/lib/errorUtils';
import { shareFile } from '@/lib/shareFile';
import { pickDocumentSafe, type PickerFailure } from '@/lib/mediaPicker';
import { resetListenerHeartbeatCache } from '@/lib/notificationListenerHealth';
import {
    decryptWithKey,
    decryptWithPassphrase,
    encryptString,
    isEncryptedBackup,
    parseEnvelope,
    type KdfParams,
} from '@/lib/backupCrypto';
import { db, execute, runInTransaction } from '@/db/client';
import { repairDanglingReferences, reportDanglingRepair, type DanglingRepairReport } from '@/db/danglingReferenceRepair';
import {
    accounts,
    categories,
    subcategories,
    transactions,
    creditCards,
    invoices,
    invoicePayments,
    recurringTransactions,
    recurringTransactionTags,
    installments,
    financings,
    financingAdjustments,
    people,
    personalLoans,
    personalLoanEvents,
    budgets,
    budgetCategories,
    budgetSubcategoryLimits,
    budgetOrigins,
    budgetSubcategories,
    goals,
    goalDeposits,
    settings,
    autoCategorizationRules,
    tags,
    transactionTags,
    pendingNotifications,
    healthAnalyses,
    reservas,
    reservaMovements,
} from '@/db/schema';
import { migrateLegacyRecurringData } from '@/db/repositories/recurringRepository';
import { envelopeVersionToWrite } from '@/lib/sync/envelopeVersion';
import journal from '@/drizzle/meta/_journal.json';

// Versão de schema = maior índice do journal de migrações Drizzle. Viaja no envelope pra
// um restore recusar dado de um app MAIS NOVO (schema com colunas que este app ainda não
// tem) em vez de inserir às cegas e corromper silenciosamente. Migrações são aditivas
// (nunca renomeiam/reaproveitam coluna — ver docs/adr/0030), então schema igual ou mais
// velho é sempre seguro de restaurar sobre o atual. É o mesmo carimbo que o sync
// multi-dispositivo usará para a compatibilidade de mão única entre aparelhos.
const CURRENT_SCHEMA_VERSION = Math.max(...journal.entries.map((e) => e.idx));

// ─── Table map for backup ────────────────────────────────────

const tableMap = {
    accounts,
    categories,
    subcategories,
    transactions,
    creditCards,
    invoices,
    invoicePayments,
    recurringTransactions,
    recurringTransactionTags,
    installments,
    financings,
    financingAdjustments,
    people,
    personalLoans,
    personalLoanEvents,
    budgets,
    budgetCategories,
    budgetSubcategoryLimits,
    budgetOrigins,
    budgetSubcategories,
    goals,
    goalDeposits,
    settings,
    autoCategorizationRules,
    tags,
    transactionTags,
    pendingNotifications,
    healthAnalyses,
    reservas,
    reservaMovements,
} as const;

type TableName = keyof typeof tableMap;

interface BackupArchiveResult {
    rowCount: number;
    tableCount: number;
}

export const BACKUP_APP_NAME = 'KONTTO';
const LEGACY_BACKUP_APP_NAMES = new Set(['FINAPP', BACKUP_APP_NAME]);

/**
 * O conteúdo escolhido é um backup do Kontto (aberto .json ou cifrado .kontto)?
 * Só reconhece o arquivo — quem restaura é o `applyBackupContent`. Serve pra tela de
 * importação desviar quem confundiu "importar planilha" com "restaurar backup" em vez
 * de responder "não reconhecemos o arquivo" (confusão relatada por usuários).
 */
export function isKonttoBackupContent(content: string): boolean {
    if (isEncryptedBackup(content)) return true;
    if (!content.trimStart().startsWith('{')) return false;
    try {
        const payload = JSON.parse(content);
        return !!payload && LEGACY_BACKUP_APP_NAMES.has(payload.app) && !!payload.data;
    } catch {
        return false;
    }
}

const TABLE_ORDER: TableName[] = [
    'accounts',
    'categories',
    'subcategories',
    'tags',
    'creditCards',
    'reservas',
    'goals',
    'budgets',
    'budgetCategories',
    'budgetSubcategoryLimits',
    'budgetOrigins',
    'budgetSubcategories',
    'invoices',
    'installments',
    'financings',
    'people',
    'personalLoans',
    'transactions',
    'financingAdjustments',
    'personalLoanEvents',
    'transactionTags',
    'invoicePayments',
    'recurringTransactions',
    'recurringTransactionTags',
    'autoCategorizationRules',
    'goalDeposits',
    'reservaMovements',
    'pendingNotifications',
    'healthAnalyses',
    'settings',
];

// Tabelas com soft-delete (deletedAt): a linha excluída fica fora do backup, A NÃO SER que
// alguma linha exportada ainda aponte pra ela (ver includeReferencedParents, ADR 0165).
const SOFT_DELETE_TABLES = new Set<TableName>([
    'accounts', 'creditCards', 'categories', 'subcategories',
    'transactions', 'recurringTransactions', 'goals', 'reservas',
]);

// ─── Helpers ─────────────────────────────────────────────────

function timestamp(): string {
    return format(new Date(), 'yyyy-MM-dd_HHmmss');
}

function rebuildDedupeKey(row: Record<string, any>): string {
    const normalize = (v?: string | null) =>
        (v ?? '').replace(/\|/g, '¦').replace(/[\r\n\t]+/g, ' ').trim().toLowerCase();
    return [
        normalize(row.rawPackage),
        normalize(row.bankId),
        normalize(row.transactionType),
        String(Math.round((row.amount ?? 0) * 100)),
        normalize(row.date),
        normalize(row.description),
        normalize(row.cardLastDigits),
        normalize(row.rawText),
    ].join('|');
}

/** Mensagem para o usuário quando o seletor nem chega a abrir (ver lib/mediaPicker). */
function pickerFailureMessage(reason: PickerFailure): string {
    if (reason === 'no-file-app') {
        return 'Seu aparelho não tem um app de arquivos ativo para escolher o backup. Ative o app "Arquivos" nas configurações do Android e tente de novo.';
    }
    return 'O seletor de arquivos não abriu. Feche o Kontto por completo, abra de novo e tente mais uma vez.';
}

/** `uri: null` = o usuário cancelou o seletor (não é erro). */
export type PickedFile = { ok: true; uri: string | null } | { ok: false; error: string };

async function pickFile(types: string[]): Promise<PickedFile> {
    const picked = await pickDocumentSafe({ type: types, copyToCacheDirectory: true }, 'backup-restore');
    if (!picked.ok) return { ok: false, error: pickerFailureMessage(picked.reason) };
    const result = picked.data;
    if (result.canceled || !result.assets?.[0]) return { ok: true, uri: null };
    return { ok: true, uri: result.assets[0].uri };
}

// ─── Purge Soft-Deleted Records ─────────────────────────────

/** Quantas linhas o DELETE removeu. O driver pode não reportar (o proxy dos testes não
 *  reporta) — sem o fallback a contagem do purge virava NaN. */
function affected(result: unknown): number {
    return (result as { rowsAffected?: number } | null | undefined)?.rowsAffected ?? 0;
}

/**
 * "Purgar registros excluídos": remove EM DEFINITIVO o que está na lixeira (linhas com
 * `deletedAt`) sem deixar referência pendurada para trás. Três regras, nesta ordem:
 *
 * 1. CASCATA — o que só existe por causa da linha que vai sumir, e que já está invisível,
 *    vai junto: junção de tags, movimento de caixinha, depósito de meta, limite por
 *    subcategoria. Ficar seria lixo com FK apontando pro vazio.
 * 2. DESVINCULA — referência OPCIONAL de linha VIVA para linha que vai sumir vira NULL.
 *    É o que os próprios `deleteX` já fazem na origem; repetir aqui cobre as bases que
 *    carregam o vínculo de antes daqueles fixes.
 * 3. PULA O PAI AINDA REFERENCIADO — conta com transação viva, cartão com fatura,
 *    categoria com orçamento. Apagar exigiria perder informação (transação sem conta) ou
 *    levar junto dado VISÍVEL, e nenhum dos dois é o que "esvaziar a lixeira" promete. A
 *    linha fica soft-deletada, invisível, como já estava.
 *
 * Categoria e subcategoria DE SISTEMA (`isSystem = 1`) nunca são purgadas: a linha
 * soft-deletada é o TOMBSTONE que impede o seeder de recriá-la no boot seguinte (ADR 0060
 * — mesmo papel do `deletedAt` em auto_categorization_rules, que também nunca é purgado).
 * Sem o tombstone, apagar "Delivery" e purgar fazia a subcategoria VOLTAR no próximo boot.
 *
 * Roda com FK LIGADA de propósito — antes era `foreignKeys: 'off'`. Se sobrar alguma
 * referência que este código não previu, a transação estoura e faz ROLLBACK: o usuário vê
 * "Falha ao limpar" e a base fica intacta, em vez de ganhar em silêncio uma órfã que só
 * estoura semanas depois, na próxima escrita que reusar aquele id (Sentry REACT-NATIVE-7).
 */
export async function purgeSoftDeletes(): Promise<number> {
    return runInTransaction(async () => {
        let total = 0;

        const deletedTransactions = db
            .select({ id: transactions.id })
            .from(transactions)
            .where(isNotNull(transactions.deletedAt));
        const deletedRecurring = db
            .select({ id: recurringTransactions.id })
            .from(recurringTransactions)
            .where(isNotNull(recurringTransactions.deletedAt));
        const deletedGoals = db
            .select({ id: goals.id })
            .from(goals)
            .where(isNotNull(goals.deletedAt));
        const deletedReservas = db
            .select({ id: reservas.id })
            .from(reservas)
            .where(isNotNull(reservas.deletedAt));
        // Só as do usuário: as de sistema ficam como tombstone do seeder (ver o doc acima).
        const purgableSubcategories = db
            .select({ id: subcategories.id })
            .from(subcategories)
            .where(and(isNotNull(subcategories.deletedAt), eq(subcategories.isSystem, 0)));

        // ── 1. Desvincula o que aponta pras TRANSAÇÕES que vão sumir ─────────────
        // Todas opcionais: a linha que fica (notificação já processada, depósito de meta,
        // abatimento de financiamento, evento de empréstimo) continua válida sem o vínculo.
        await db
            .update(pendingNotifications)
            .set({ createdTransactionId: null })
            .where(inArray(pendingNotifications.createdTransactionId, deletedTransactions));
        await db
            .update(goalDeposits)
            .set({ transactionId: null })
            .where(inArray(goalDeposits.transactionId, deletedTransactions));
        // Rendimento de caixinha (ADR 0161): a receita sai junto com o movimento, então aqui só
        // sobra referência em caso de borda — sem ela o movimento continua valendo.
        await db
            .update(reservaMovements)
            .set({ transactionId: null })
            .where(inArray(reservaMovements.transactionId, deletedTransactions));
        await db
            .update(financingAdjustments)
            .set({ transactionId: null })
            .where(inArray(financingAdjustments.transactionId, deletedTransactions));
        await db
            .update(personalLoanEvents)
            .set({ transactionId: null })
            .where(inArray(personalLoanEvents.transactionId, deletedTransactions));
        await db
            .update(personalLoanEvents)
            .set({ excessTransactionId: null })
            .where(inArray(personalLoanEvents.excessTransactionId, deletedTransactions));

        // Junções de tag: sem soft-delete próprio, morrem com a linha-pai. Junction órfã,
        // além de lixo, distorce a união de tags de contrato (getFinancingTags/
        // getPersonalLoanTags leem inclusive as soft-deletadas).
        await db.delete(transactionTags).where(inArray(transactionTags.transactionId, deletedTransactions));
        await db
            .delete(recurringTransactionTags)
            .where(inArray(recurringTransactionTags.recurringId, deletedRecurring));

        total += affected(await db.delete(transactions).where(isNotNull(transactions.deletedAt)));
        total += affected(await db
            .delete(recurringTransactions)
            .where(isNotNull(recurringTransactions.deletedAt)));

        // ── 2. Metas e caixinhas: o histórico é EXCLUSIVO delas, some junto ──────
        total += affected(await db.delete(goalDeposits).where(inArray(goalDeposits.goalId, deletedGoals)));
        total += affected(await db.delete(goals).where(isNotNull(goals.deletedAt)));
        total += affected(await db
            .delete(reservaMovements)
            .where(inArray(reservaMovements.reservaId, deletedReservas)));
        total += affected(await db.delete(reservas).where(isNotNull(reservas.deletedAt)));

        // ── 3. Subcategorias do usuário: desvincula tudo e apaga ─────────────────
        // `deleteSubcategory` já zera transação e regra na origem; recorrência,
        // parcelamento e financiamento ficavam apontando — e viravam órfãos aqui.
        const nulledAt = new Date().toISOString();
        await db
            .update(transactions)
            .set({ subcategoryId: null, updatedAt: nulledAt })
            .where(inArray(transactions.subcategoryId, purgableSubcategories));
        await db
            .update(recurringTransactions)
            .set({ subcategoryId: null, updatedAt: nulledAt })
            .where(inArray(recurringTransactions.subcategoryId, purgableSubcategories));
        await db
            .update(installments)
            .set({ subcategoryId: null, updatedAt: nulledAt })
            .where(inArray(installments.subcategoryId, purgableSubcategories));
        await db
            .update(financings)
            .set({ subcategoryId: null, updatedAt: nulledAt })
            .where(inArray(financings.subcategoryId, purgableSubcategories));
        await db
            .update(autoCategorizationRules)
            .set({ subcategoryId: null })
            .where(inArray(autoCategorizationRules.subcategoryId, purgableSubcategories));
        // O recorte por subcategoria (ADR 0067) não sobrevive à subcategoria: sem ela o
        // limite não tem o que vigiar e já não aparece na tela do orçamento.
        total += affected(await db
            .delete(budgetSubcategoryLimits)
            .where(inArray(budgetSubcategoryLimits.subcategoryId, purgableSubcategories)));
        // O recorte por subcategoria (ADR 0110) também: a linha aponta a subcategoria por FK.
        // Consequência aceita: se era a ÚNICA da categoria no recorte, a categoria volta a
        // contar inteira — o expurgo é ação explícita, e a alternativa (segurar a
        // subcategoria morta pra sempre) esconderia lixo que a pessoa mandou apagar.
        total += affected(await db
            .delete(budgetSubcategories)
            .where(inArray(budgetSubcategories.subcategoryId, purgableSubcategories)));
        total += affected(await db
            .delete(subcategories)
            .where(and(isNotNull(subcategories.deletedAt), eq(subcategories.isSystem, 0))));

        // ── 4. Categorias do usuário, só as que ninguém mais referencia ──────────
        // `deleteCategory` reaponta transação, recorrência, parcelamento, financiamento,
        // regra e orçamento pra categoria de fallback, então o normal é não sobrar nada.
        // Ainda assim conferimos: uma subcategoria DE SISTEMA movida pra cá por
        // `mergeCategories` vira tombstone e segura a categoria — e é melhor deixá-la
        // soft-deletada do que apagar a mãe de uma linha que precisa existir.
        const orphanCategories = await db
            .select({ id: categories.id })
            .from(categories)
            .where(and(
                isNotNull(categories.deletedAt),
                eq(categories.isSystem, 0),
                notExists(db.select({ n: sql`1` }).from(subcategories)
                    .where(eq(subcategories.categoryId, categories.id))),
                notExists(db.select({ n: sql`1` }).from(transactions)
                    .where(eq(transactions.categoryId, categories.id))),
                notExists(db.select({ n: sql`1` }).from(recurringTransactions)
                    .where(eq(recurringTransactions.categoryId, categories.id))),
                notExists(db.select({ n: sql`1` }).from(installments)
                    .where(eq(installments.categoryId, categories.id))),
                notExists(db.select({ n: sql`1` }).from(financings)
                    .where(eq(financings.categoryId, categories.id))),
                notExists(db.select({ n: sql`1` }).from(budgets)
                    .where(eq(budgets.categoryId, categories.id))),
                notExists(db.select({ n: sql`1` }).from(budgetCategories)
                    .where(eq(budgetCategories.categoryId, categories.id))),
                notExists(db.select({ n: sql`1` }).from(autoCategorizationRules)
                    .where(eq(autoCategorizationRules.categoryId, categories.id))),
            ));
        if (orphanCategories.length > 0) {
            total += affected(await db
                .delete(categories)
                .where(inArray(categories.id, orphanCategories.map((row) => row.id))));
        }

        // ── 4.5. Faturas VAZIAS de cartão excluído ───────────────────────────────
        // A tela do cartão grava a fatura do mês exibido e a dos dois vizinhos só de abrir
        // (pré-carga), então um cartão visitado e depois excluído fica preso aqui pra sempre:
        // a regra 5 abaixo só apaga cartão sem NENHUMA fatura. Some com as que nasceram da
        // leitura e nunca viraram nada — sem compra, sem pagamento, sem total manual, sem
        // conferência, sem sobra e sem outra fatura rolada delas. Fatura com qualquer
        // conteúdo continua prendendo o cartão, que é o que o ADR 0068 quer.
        // Auto-join: a fatura seguinte que rolou a sobra desta.
        const nextInvoices = alias(invoices, 'next_invoices');
        const emptyInvoicesOfDeletedCards = await db
            .select({ id: invoices.id })
            .from(invoices)
            .innerJoin(creditCards, eq(invoices.creditCardId, creditCards.id))
            .where(and(
                isNotNull(creditCards.deletedAt),
                eq(invoices.totalAmount, 0),
                eq(invoices.paidAmount, 0),
                eq(invoices.remainderAmount, 0),
                eq(invoices.isManuallySet, 0),
                isNull(invoices.manualTotal),
                isNull(invoices.reconciledAt),
                notExists(db.select({ n: sql`1` }).from(transactions)
                    .where(eq(transactions.invoiceId, invoices.id))),
                notExists(db.select({ n: sql`1` }).from(invoicePayments)
                    .where(eq(invoicePayments.invoiceId, invoices.id))),
                // Fatura que outra rolou (sobra do mês anterior) guarda a origem: apagá-la
                // deixaria a seguinte apontando pro vazio.
                notExists(db.select({ n: sql`1` }).from(nextInvoices)
                    .where(eq(nextInvoices.rolledFromInvoiceId, invoices.id))),
            ));

        if (emptyInvoicesOfDeletedCards.length > 0) {
            total += affected(await db
                .delete(invoices)
                .where(inArray(invoices.id, emptyInvoicesOfDeletedCards.map((row) => row.id))));
        }

        // ── 5. Cartões: só os que já não têm fatura, parcelamento nem lançamento ─
        // Fatura e parcelamento são NOT NULL no cartão e não têm soft-delete: apagar o
        // cartão os deixaria órfãos (era a fatura zumbi do ADR 0068, que o backfill do
        // boot ainda cura). Compra confirmada sobrevive à exclusão do cartão de propósito
        // (ver deleteCreditCard) — então o cartão usado fica em soft-delete pra sempre.
        const orphanCards = await db
            .select({ id: creditCards.id })
            .from(creditCards)
            .where(and(
                isNotNull(creditCards.deletedAt),
                notExists(db.select({ n: sql`1` }).from(invoices)
                    .where(eq(invoices.creditCardId, creditCards.id))),
                notExists(db.select({ n: sql`1` }).from(installments)
                    .where(eq(installments.creditCardId, creditCards.id))),
                notExists(db.select({ n: sql`1` }).from(transactions)
                    .where(eq(transactions.creditCardId, creditCards.id))),
                notExists(db.select({ n: sql`1` }).from(recurringTransactions)
                    .where(eq(recurringTransactions.creditCardId, creditCards.id))),
                // Origem de orçamento (ADR 0103): o vínculo sobrevive ao soft-delete de
                // propósito — some daqui e o recorte esvazia, e recorte vazio significa
                // TODAS as origens: o orçamento alargaria sozinho de "só o cartão" para
                // "tudo", calado.
                notExists(db.select({ n: sql`1` }).from(budgetOrigins)
                    .where(eq(budgetOrigins.creditCardId, creditCards.id))),
            ));
        if (orphanCards.length > 0) {
            const cardIds = orphanCards.map((row) => row.id);
            // Caixinha turbinada: o vínculo é opcional e o cartão está indo embora.
            await db
                .update(reservas)
                .set({ creditCardId: null, updatedAt: nulledAt })
                .where(inArray(reservas.creditCardId, cardIds));
            total += affected(await db.delete(creditCards).where(inArray(creditCards.id, cardIds)));
        }

        // ── 6. Contas: só as que já não têm nada apontando ───────────────────────
        // O dialog de excluir conta promete que "todas as transações desta conta serão
        // mantidas" — então a conta usada continua sendo referência viva e NÃO pode sumir:
        // zerar `account_id` das transações apagaria de onde o dinheiro saiu.
        const orphanAccounts = await db
            .select({ id: accounts.id })
            .from(accounts)
            .where(and(
                isNotNull(accounts.deletedAt),
                notExists(db.select({ n: sql`1` }).from(transactions).where(or(
                    eq(transactions.accountId, accounts.id),
                    eq(transactions.transferDestinationAccountId, accounts.id),
                ))),
                notExists(db.select({ n: sql`1` }).from(recurringTransactions).where(or(
                    eq(recurringTransactions.accountId, accounts.id),
                    eq(recurringTransactions.transferDestinationAccountId, accounts.id),
                ))),
                notExists(db.select({ n: sql`1` }).from(goals)
                    .where(eq(goals.accountId, accounts.id))),
                notExists(db.select({ n: sql`1` }).from(goalDeposits)
                    .where(eq(goalDeposits.accountId, accounts.id))),
                notExists(db.select({ n: sql`1` }).from(personalLoans)
                    .where(eq(personalLoans.accountId, accounts.id))),
                notExists(db.select({ n: sql`1` }).from(reservas)
                    .where(eq(reservas.accountId, accounts.id))),
                notExists(db.select({ n: sql`1` }).from(invoicePayments)
                    .where(eq(invoicePayments.accountId, accounts.id))),
                notExists(db.select({ n: sql`1` }).from(financings)
                    .where(eq(financings.paymentAccountId, accounts.id))),
                // Mesma razão do cartão acima (ADR 0103): recorte que perde a linha vira
                // recorte vazio, que significa TODAS as origens.
                notExists(db.select({ n: sql`1` }).from(budgetOrigins)
                    .where(eq(budgetOrigins.accountId, accounts.id))),
            ));
        if (orphanAccounts.length > 0) {
            const accountIds = orphanAccounts.map((row) => row.id);
            // `deleteAccount` já desvincula na origem; isto cobre as bases que carregam a
            // referência de antes daquele fix. Conta de pagamento vazia é estado suportado.
            await db
                .update(creditCards)
                .set({ paymentAccountId: null, updatedAt: nulledAt })
                .where(inArray(creditCards.paymentAccountId, accountIds));
            total += affected(await db.delete(accounts).where(inArray(accounts.id, accountIds)));
        }

        return total;
    });
}

// ─── Full Backup ─────────────────────────────────────────────

export interface BackupEncryptionInput {
    key: Uint8Array;
    salt: Uint8Array;
    params?: KdfParams;
}

export interface BackupContent {
    content: string;
    filename: string;
    rowCount: number;
    tableCount: number;
}

export interface BackupSnapshotMeta {
    /**
     * Instante em que o espaço de sync ficou em dia IMEDIATAMENTE antes deste snapshot
     * (`syncBeforeSnapshot`). Presente = este backup está contido no espaço, então restaurá-lo
     * e depois entrar na sincronização devolve o mesmo dado, sem perda. Ausente = backup tirado
     * com o sync desligado, sem rede ou com o ciclo falhando: aí o app tem que perguntar.
     */
    syncedAt?: string | null;
}

/** Serializa o banco e (opcionalmente) cifra — sem compartilhar nem gravar. */
export async function createBackupContent(
    encryption?: BackupEncryptionInput,
    meta?: BackupSnapshotMeta,
): Promise<BackupContent> {
    const backup: Record<string, any[]> = {};

    // Collect active IDs from soft-delete tables to cascade-filter dependents
    const activeIds: Partial<Record<TableName, Set<string>>> = {};

    // Snapshot CONSISTENTE: as leituras vão todas numa transação. Sem ela, um escritor
    // concorrente na mesma conexão (aplicador do sync, listener de notificações, restore)
    // entra ENTRE dois selects e o backup sai "rasgado" — ex.: transação órfã de uma conta
    // lida antes dela existir, que o restore (FK OFF) reinsere como lixo invisível. De
    // quebra, se um RESTORE estiver com transação aberta, o BEGIN aqui falha limpo em vez
    // de fotografar (e subir pro Drive) um banco meio-apagado não-commitado.
    await runInTransaction(async () => {
        for (const name of TABLE_ORDER) {
            const table = tableMap[name];
            let rows: any[];

            if (SOFT_DELETE_TABLES.has(name)) {
                rows = await db.select().from(table).where(isNull((table as any).deletedAt));
                activeIds[name] = new Set(rows.map((r: any) => r.id));
                // TOMBSTONE do seeder: categoria/subcategoria DE SISTEMA apagada viaja no
                // backup mesmo soft-deletada (invisível no app) porque é ela que impede o
                // seed de recriar a linha no aparelho novo — sem isso, restaurar
                // RESSUSCITAVA a subcategoria que o usuário tinha apagado. Mesma raiz do
                // purge (ver purgeSoftDeletes) e mesmo tratamento que autoCategorizationRules
                // já recebe. Fica FORA de `activeIds`: nenhum dependente pode apontar pra ela.
                if (name === 'categories' || name === 'subcategories') {
                    let tombstones = await db
                        .select()
                        .from(table)
                        .where(and(isNotNull((table as any).deletedAt), eq((table as any).isSystem, 1)));
                    if (name === 'subcategories') {
                        // Categoria é NOT NULL: o tombstone só entra se a mãe também veio
                        // (uma sub de sistema pode ter ido parar numa categoria do usuário
                        // via mergeCategories, e essa categoria pode não estar no backup).
                        const exportedCategoryIds = new Set(backup.categories?.map((r: any) => r.id));
                        tombstones = tombstones.filter((r: any) => exportedCategoryIds.has(r.categoryId));
                    }
                    rows = [...rows, ...tombstones];
                }
            } else {
                rows = await db.select().from(table);
            }

            // Cascade-filter: exclude dependents of soft-deleted parents (NOT NULL FKs)
            switch (name) {
                case 'creditCards':
                    // Conta de pagamento é opcional: se ficou de fora do backup (soft-delete),
                    // anula em vez de levar a referência pendurada. O restore roda com FK OFF
                    // e ACEITARIA o id órfão — a conta reapareceria só na hora de gravar a
                    // fatura pendente, estourando FOREIGN KEY em toda leitura de fatura no
                    // aparelho novo (Sentry REACT-NATIVE-7). Mesmo tratamento que `reservas`
                    // dá ao cartão turbinado.
                    rows = rows.map((r: any) => ({
                        ...r,
                        paymentAccountId:
                            r.paymentAccountId && activeIds.accounts!.has(r.paymentAccountId)
                                ? r.paymentAccountId
                                : null,
                    }));
                    break;
                case 'invoices':
                    rows = rows.filter((r: any) => activeIds.creditCards!.has(r.creditCardId));
                    break;
                case 'installments':
                    rows = rows.filter((r: any) => activeIds.creditCards!.has(r.creditCardId));
                    break;
                case 'invoicePayments': {
                    const activeInvoiceIds = new Set(backup.invoices?.map((r: any) => r.id));
                    rows = rows.filter((r: any) => activeInvoiceIds.has(r.invoiceId));
                    break;
                }
                case 'financings':
                    rows = rows.filter(
                        (r: any) =>
                            activeIds.accounts!.has(r.paymentAccountId)
                            && activeIds.categories!.has(r.categoryId),
                    ).filter(
                        (r: any) => !r.subcategoryId || activeIds.subcategories!.has(r.subcategoryId),
                    );
                    break;
                case 'financingAdjustments': {
                    const activeFinancingIds = new Set(backup.financings?.map((r: any) => r.id));
                    const activeTransactionIds = new Set(backup.transactions?.map((r: any) => r.id));
                    rows = rows.filter(
                        (r: any) =>
                            activeFinancingIds.has(r.financingId)
                            && (!r.transactionId || activeTransactionIds.has(r.transactionId)),
                    );
                    break;
                }
                case 'personalLoans':
                    // Conta é opcional (empréstimo já existente); se existir, precisa estar ativa.
                    rows = rows.filter(
                        (r: any) => !r.accountId || activeIds.accounts!.has(r.accountId),
                    );
                    break;
                case 'personalLoanEvents': {
                    const activeLoanIds = new Set(backup.personalLoans?.map((r: any) => r.id));
                    const activeTransactionIds = new Set(backup.transactions?.map((r: any) => r.id));
                    // O evento é o razão (fonte de verdade do saldo) — preserva mesmo que a
                    // transação vinculada tenha sido soft-deletada (ex.: conta removida);
                    // só anula as referências que não resolvem, pra não criar FK pendente
                    // no restore. Sem isso, perder o evento reabriria o empréstimo com saldo
                    // errado ao reimportar.
                    rows = rows
                        .filter((r: any) => activeLoanIds.has(r.loanId))
                        .map((r: any) => ({
                            ...r,
                            transactionId:
                                r.transactionId && activeTransactionIds.has(r.transactionId) ? r.transactionId : null,
                            excessTransactionId:
                                r.excessTransactionId && activeTransactionIds.has(r.excessTransactionId)
                                    ? r.excessTransactionId
                                    : null,
                        }));
                    break;
                }
                case 'transactionTags':
                    rows = rows.filter((r: any) => activeIds.transactions!.has(r.transactionId));
                    break;
                case 'recurringTransactionTags':
                    // Tags da SÉRIE (fonte de verdade — propagam às ocorrências futuras); sem
                    // elas no backup, recorrências restauradas geravam ocorrências sem tag.
                    rows = rows.filter((r: any) => activeIds.recurringTransactions!.has(r.recurringId));
                    break;
                case 'goalDeposits': {
                    const activeTransactionIds = new Set(backup.transactions?.map((r: any) => r.id));
                    // O depósito é a fonte de verdade do progresso da meta — preserva mesmo
                    // sem a transação de desconto (anula referências que não resolvem, como
                    // conta/transação removidas, pra não criar FK pendente no restore).
                    rows = rows
                        .filter((r: any) => activeIds.goals!.has(r.goalId))
                        .map((r: any) => ({
                            ...r,
                            accountId: r.accountId && activeIds.accounts!.has(r.accountId) ? r.accountId : null,
                            transactionId:
                                r.transactionId && activeTransactionIds.has(r.transactionId) ? r.transactionId : null,
                        }));
                    break;
                }
                case 'reservas':
                    // Conta é NOT NULL; cartão turbinado é opcional (anula se foi removido).
                    rows = rows
                        .filter((r: any) => activeIds.accounts!.has(r.accountId))
                        .map((r: any) => ({
                            ...r,
                            creditCardId:
                                r.creditCardId && activeIds.creditCards!.has(r.creditCardId) ? r.creditCardId : null,
                        }));
                    break;
                case 'reservaMovements': {
                    const activeReservaIds = new Set(backup.reservas?.map((r: any) => r.id));
                    const activeTransactionIds = new Set(backup.transactions?.map((r: any) => r.id));
                    // O rendimento vale sem a receita; o vínculo só fica se ela veio no backup.
                    rows = rows
                        .filter((r: any) => activeReservaIds.has(r.reservaId))
                        .map((r: any) => ({
                            ...r,
                            transactionId:
                                r.transactionId && activeTransactionIds.has(r.transactionId) ? r.transactionId : null,
                        }));
                    break;
                }
                case 'budgets':
                    rows = rows.filter((r: any) => activeIds.categories!.has(r.categoryId));
                    break;
                case 'budgetCategories': {
                    // A junction é a FONTE DA VERDADE do conjunto de categorias do orçamento
                    // (budgetRepository lê SÓ por ela) — sem ela no backup, todo orçamento
                    // restaurado em aparelho novo ficava sem categoria (gasto rastreado = 0).
                    const activeBudgetIds = new Set(backup.budgets?.map((r: any) => r.id));
                    rows = rows.filter(
                        (r: any) => activeBudgetIds.has(r.budgetId) && activeIds.categories!.has(r.categoryId),
                    );
                    break;
                }
                case 'budgetSubcategoryLimits': {
                    // Linha do recorte (ADR 0067): não existe sem o pai nem sem a
                    // subcategoria — cai fora se qualquer um dos dois não veio.
                    const limitBudgetIds = new Set(backup.budgets?.map((r: any) => r.id));
                    rows = rows.filter(
                        (r: any) => limitBudgetIds.has(r.budgetId) && activeIds.subcategories!.has(r.subcategoryId),
                    );
                    break;
                }
                case 'budgetSubcategories': {
                    // Recorte por subcategoria (ADR 0110): como o limite, não existe sem o
                    // pai nem sem a subcategoria. Cai fora se qualquer um dos dois não veio.
                    const scopeBudgetIds = new Set(backup.budgets?.map((r: any) => r.id));
                    rows = rows.filter(
                        (r: any) => scopeBudgetIds.has(r.budgetId) && activeIds.subcategories!.has(r.subcategoryId),
                    );
                    break;
                }
                case 'budgetOrigins': {
                    // Origem do recorte (ADR 0103): não existe sem o pai nem sem a conta/
                    // cartão que ela aponta. **Cai fora inteira** se a origem não veio — o
                    // que NÃO pode acontecer é a linha sumir e o recorte esvaziar, porque
                    // recorte vazio significa TODAS as origens e o orçamento restaurado
                    // alargaria sozinho de "só o cartão" para "tudo".
                    const originBudgetIds = new Set(backup.budgets?.map((r: any) => r.id));
                    rows = rows.filter((r: any) => {
                        if (!originBudgetIds.has(r.budgetId)) return false;
                        if (r.accountId) return activeIds.accounts!.has(r.accountId);
                        if (r.creditCardId) return activeIds.creditCards!.has(r.creditCardId);
                        return false;
                    });
                    break;
                }
                case 'autoCategorizationRules':
                    rows = rows.filter((r: any) => activeIds.categories!.has(r.categoryId));
                    break;
                case 'pendingNotifications':
                    // Strip computed dedupeKey — recomputed on restore
                    rows = rows.map(({ dedupeKey: _, ...rest }) => rest);
                    break;
            }

            backup[name] = rows;
        }
        await includeReferencedParents(backup);
    });

    const payload = {
        version: 1,
        schemaVersion: CURRENT_SCHEMA_VERSION,
        createdAt: new Date().toISOString(),
        app: BACKUP_APP_NAME,
        // Só entra quando existe: um backup antigo (sem a chave) e um tirado fora de compasso
        // têm que ser indistinguíveis — os dois significam "não posso garantir, pergunte".
        ...(meta?.syncedAt ? { syncedAt: meta.syncedAt } : {}),
        data: backup,
    };

    // Compacto: a indentação era 28% do arquivo e nenhum leitor depende dela (ADR 0177).
    const json = JSON.stringify(payload);
    const rowCount = Object.values(backup).reduce((sum, rows) => sum + rows.length, 0);
    const tableCount = Object.keys(backup).length;

    if (encryption) {
        // Na versão que o sync negociou com os outros aparelhos (ADR 0181): com um app antigo no
        // espaço o backup sai v1, pra ele ainda conseguir restaurar; sozinho, no v2 compacto.
        const envelope = encryptString(json, encryption.key, encryption.salt, encryption.params, await envelopeVersionToWrite());
        return { content: envelope, filename: `kontto_backup_${timestamp()}.kontto`, rowCount, tableCount };
    }
    return { content: json, filename: `kontto_backup_${timestamp()}.json`, rowCount, tableCount };
}

/**
 * Fecha o backup sob as referências (ADR 0165): todo pai que uma linha exportada ainda aponta
 * entra no arquivo como está no banco, inclusive EXCLUÍDO.
 *
 * Excluir conta ou cartão é soft-delete e mantém os lançamentos efetivados vivos (a despesa
 * da conta, a compra no cartão, o pagamento da fatura na conta pagadora). Sem isto o export
 * deixava o pai de fora e levava os filhos; o restore, que grava com FK OFF, criava a
 * referência pendurada: editar o lançamento estourava FOREIGN KEY (Sentry REACT-NATIVE-7), a
 * fatura do cartão dava "Credit card not found" (REACT-NATIVE-2V) e a compra do cartão
 * excluído voltava pros relatórios. Com o pai no arquivo, o aparelho novo fica igual ao de
 * origem: excluído e invisível como era.
 *
 * As arestas saem das FKs do schema, então tabela nova entra sozinha. Repete até nenhuma
 * aresta puxar linha nova (a fatura puxada traz o cartão dela). Id que nem o banco de origem
 * tem fica como está: é órfã antiga, e o restore repara (repairDanglingReferences).
 */
async function includeReferencedParents(backup: Record<string, any[]>): Promise<void> {
    const nameByTable = new Map<unknown, TableName>(TABLE_ORDER.map((name) => [tableMap[name], name]));
    const keyOf = (name: TableName, column: SQLiteColumn) =>
        Object.entries(getTableColumns(tableMap[name] as SQLiteTable)).find(([, c]) => c.name === column.name)?.[0];

    const edges = TABLE_ORDER.flatMap((child) =>
        getTableConfig(tableMap[child] as SQLiteTable).foreignKeys.flatMap((fk) => {
            const ref = fk.reference();
            const parent = nameByTable.get(ref.foreignTable);
            if (!parent || ref.columns.length !== 1) return [];
            const childKey = keyOf(child, ref.columns[0]);
            const parentKey = keyOf(parent, ref.foreignColumns[0]);
            if (!childKey || !parentKey) return [];
            return [{ child, childKey, parent, parentKey, parentColumn: ref.foreignColumns[0] }];
        }),
    );

    for (let pulled = true; pulled;) {
        pulled = false;
        for (const edge of edges) {
            const present = new Set((backup[edge.parent] ?? []).map((r) => r[edge.parentKey]));
            const missing = [...new Set(
                (backup[edge.child] ?? [])
                    .map((r) => r[edge.childKey])
                    .filter((value) => value != null && !present.has(value)),
            )];
            for (let i = 0; i < missing.length; i += 500) {
                const rows = await db
                    .select()
                    .from(tableMap[edge.parent] as SQLiteTable)
                    .where(inArray(edge.parentColumn, missing.slice(i, i + 500)));
                if (rows.length > 0) {
                    backup[edge.parent] = [...(backup[edge.parent] ?? []), ...rows];
                    pulled = true;
                }
            }
        }
    }
}

/** Cria o backup e abre o compartilhamento local (share sheet). */
export async function createBackup(encryption?: BackupEncryptionInput): Promise<BackupArchiveResult> {
    const { content, filename, rowCount, tableCount } = await createBackupContent(encryption);
    await shareFile(filename, content);
    return { rowCount, tableCount };
}

// ─── Restore Backup ──────────────────────────────────────────

export interface RestoreBackupOptions {
    /** Chave cacheada deste aparelho (tentada primeiro p/ backups cifrados). */
    getKey?: () => Promise<Uint8Array | null>;
    /** Pede a senha ao usuario quando a chave cacheada nao resolve (outro aparelho). */
    resolvePassphrase?: () => Promise<string | null>;
}

export interface RestoreResult {
    success: boolean;
    error?: string;
    rowCount?: number;
    tableCount?: number;
    /**
     * `syncedAt` que veio no envelope (ver BackupSnapshotMeta). Quem restaura guarda isto pra
     * que ligar a sincronização depois saiba que a base local já está contida no espaço.
     */
    syncedAt?: string | null;
}

/** Aplica um conteudo de backup (texto cru, cifrado ou aberto) ao banco local. */
export async function applyBackupContent(rawContent: string, options?: RestoreBackupOptions): Promise<RestoreResult> {
    try {
        let content = rawContent;

        // Backup cifrado (.kontto): decifra antes de parsear.
        if (isEncryptedBackup(content)) {
            const envelope = parseEnvelope(content)!;
            let decrypted: string | null = null;

            const cachedKey = options?.getKey ? await options.getKey() : null;
            if (cachedKey) {
                try {
                    decrypted = decryptWithKey(envelope, cachedKey);
                } catch {
                    decrypted = null; // chave deste aparelho nao serve (backup de outro)
                }
            }

            if (decrypted == null) {
                const passphrase = options?.resolvePassphrase ? await options.resolvePassphrase() : null;
                if (passphrase == null) return { success: false, error: 'Cancelado' };
                try {
                    decrypted = await decryptWithPassphrase(envelope, passphrase);
                } catch {
                    return { success: false, error: 'Senha incorreta ou arquivo corrompido.' };
                }
            }

            content = decrypted;
        }

        const payload = JSON.parse(content);

        if (!LEGACY_BACKUP_APP_NAMES.has(payload.app) || !payload.data) {
            return { success: false, error: 'Arquivo inválido. Não é um backup do Kontto.' };
        }

        // Recusa backup de um app MAIS NOVO (schema que este app ainda não conhece): inserir
        // dado de colunas futuras às cegas corromperia em silêncio. Backups antigos não têm
        // schemaVersion → tratados como 0 (schema mais velho, sempre seguro sobre o atual).
        const backupSchemaVersion = typeof payload.schemaVersion === 'number' ? payload.schemaVersion : 0;
        if (backupSchemaVersion > CURRENT_SCHEMA_VERSION) {
            return {
                success: false,
                error: 'Este backup foi criado por uma versão mais nova do Kontto. Atualize o app para restaurá-lo.',
            };
        }

        // FK OFF via opção do runInTransaction — o backup pode ter refs órfãs de soft-deletes
        // excluídos. O PRAGMA roda sob o mutex, antes do BEGIN: um PRAGMA solto aqui era
        // ignorado em silêncio se houvesse transação em voo na conexão (escrita concorrente),
        // e o restore rodava com FK LIGADO.
        // Apaga tudo + reinsere numa ÚNICA transação: se um lote falhar (JSON adulterado,
        // NOT NULL, ref quebrada), faz rollback e o banco original do usuário continua
        // intacto — em vez de ficar meio-apagado e sem volta.
        let restoredRows = 0;
        let restoredTables = 0;
        let danglingRepair: DanglingRepairReport | null = null;
        await runInTransaction(async () => {
            const tx = db;
            // Delete all data in reverse dependency order then insert
            const reverseOrder = [...TABLE_ORDER].reverse();
            for (const name of reverseOrder) {
                const table = tableMap[name];
                await tx.delete(table);
            }

            // Insert in dependency order
            for (const name of TABLE_ORDER) {
                const rows = payload.data[name];
                if (rows && rows.length > 0) {
                    const table = tableMap[name];
                    restoredTables += 1;
                    for (let i = 0; i < rows.length; i += 50) {
                        let batch = rows.slice(i, i + 50);

                        // Recompute dedupeKey if missing (stripped from newer backups)
                        if (name === 'pendingNotifications') {
                            batch = batch.map((row: any) => ({
                                ...row,
                                dedupeKey: row.dedupeKey || rebuildDedupeKey(row),
                            }));
                        }

                        // transaction_tags ganhou índice único (migração 0019). Backups ANTIGOS
                        // (feitos antes de 0019, quando setTransactionTags não deduplicava) ou de
                        // outro aparelho podem conter pares (transação, tag) repetidos; sem o
                        // onConflictDoNothing um único par duplicado lançaria UNIQUE constraint e
                        // faria rollback do restore INTEIRO — um backup que restaurava antes ficaria
                        // irrestaurável. FK OFF não desliga índice único. Set-membership: ignorar a
                        // repetição é semanticamente correto.
                        if (name === 'transactionTags' || name === 'recurringTransactionTags') {
                            await tx.insert(table).values(batch).onConflictDoNothing();
                        } else {
                            await tx.insert(table).values(batch);
                        }
                        restoredRows += batch.length;
                    }
                }
            }

            // Backup ANTIGO (feito antes de budget_categories entrar no backup): recria o
            // vínculo orçamento→categoria a partir da coluna legada budgets.category_id —
            // a MESMA semântica do backfill da migração 0005. Sem isso, o orçamento
            // restaurado ficava sem nenhuma categoria (o repositório lê SÓ pela junction)
            // e o gasto rastreado zerava. Idempotente: só preenche orçamento que ficou sem
            // NENHUMA linha na junction (backup novo já traz as linhas → no-op).
            await execute(
                `INSERT INTO budget_categories (id, budget_id, category_id, created_at)
                 SELECT lower(hex(randomblob(16))), id, category_id, created_at FROM budgets b
                 WHERE NOT EXISTS (SELECT 1 FROM budget_categories bc WHERE bc.budget_id = b.id)`,
            );

            // Backup tirado antes da ADR 0165 não leva a conta/o cartão excluído que um lançamento
            // vivo ainda aponta, e esses arquivos continuam no Drive e nas pastas dos usuários. Com
            // FK OFF o insert acima aceitou a referência pendurada; aqui ela volta a ter dono
            // (excluído, como no aparelho de origem), na mesma transação do restore.
            danglingRepair = await repairDanglingReferences(
                (sqlText, params) => execute(sqlText, params as never),
            );
        }, { foreignKeys: 'off' });
        reportDanglingRepair(danglingRepair, 'restore');

        // Pós-passo best-effort: o restore JÁ commitou — o banco É o do backup. Uma falha
        // aqui não pode virar "success: false": o usuário veria "Falha ao restaurar" com o
        // banco já trocado e, com sync ligado, o chamador (restoreUnderSync) pularia o
        // delta — o próximo ciclo de sync REVERTERIA o restore em silêncio a partir do
        // oplog velho. A migração legada re-roda no boot (app/_layout), então adiar é seguro.
        try {
            await migrateLegacyRecurringData({ force: true });
        } catch (e) {
            console.warn('Restore: migração legada adiada pro próximo boot:', e);
        }

        // A tabela settings acabou de voltar no tempo; o cache em memória do batimento
        // do listener ficou à frente e engoliria as escritas por até 30 min — o
        // batimento defasado viraria diagnóstico de captura travada falso.
        resetListenerHeartbeatCache();

        return {
            success: true,
            rowCount: restoredRows,
            tableCount: restoredTables,
            // Só string vale: um envelope adulterado/antigo com `syncedAt` de outro tipo não
            // pode virar "está em compasso" e liberar um join sem perguntar.
            syncedAt: typeof payload.syncedAt === 'string' ? payload.syncedAt : null,
        };
    } catch (e: unknown) {
        return { success: false, error: getErrorMessage(e, 'Erro ao restaurar') };
    }
}

/** Restaura a partir de um arquivo escolhido pelo usuario (document picker). */
/**
 * Abre o seletor de arquivo de backup — SEPARADO do restore de propósito.
 *
 * No iOS, apresentar o picker do sistema no mesmo tick de um `<Modal>` (o overlay de
 * progresso da tela de Backups) fazia as duas apresentações competirem; quando o picker
 * perdia, o `UIDocumentPickerViewController` era desalocado com o serviço remoto ainda
 * vivo e o DocumentManager derrubava o app ("tried to send a message to a deallocated
 * host proxy" — Sentry REACT-NATIVE-28, fatal, 3 eventos / 2 usuários). A tela escolhe
 * o arquivo primeiro e só então mostra o overlay e chama `restoreBackupFromUri`.
 */
export function pickBackupFile(): Promise<PickedFile> {
    return pickFile(['application/json', 'application/octet-stream', '*/*']);
}

/** Restaura a partir de um arquivo já escolhido (URI local, ver `pickBackupFile`). */
export async function restoreBackupFromUri(uri: string, options?: RestoreBackupOptions): Promise<RestoreResult> {
    try {
        const content = await new File(uri).text();
        return await applyBackupContent(content, options);
    } catch (e: unknown) {
        return { success: false, error: getErrorMessage(e, 'Não foi possível ler o arquivo.') };
    }
}

/** Restaura a partir de um texto ja lido (ex.: um arquivo do historico na pasta). */
export async function restoreFromText(content: string, options?: RestoreBackupOptions): Promise<RestoreResult> {
    return applyBackupContent(content, options);
}
